import { useEffect, useMemo, useRef, useState } from "react";
import { trpc } from "../../lib/trpc.js";
import type { EscolhaDeMusica } from "./preferencias.js";
import {
  TOCADOR_PARADO,
  faixaAtual,
  iniciarTocador,
  proximaFaixa,
  sincronizarFaixas,
  type EstadoTocador,
} from "./tocador.js";

/** O que a projeção aciona na trilha: ligar o som no toque. */
export interface ControleDaTrilha {
  desbloquear(): void;
}

/**
 * Meia hora: as URLs valem duas, e cada faixa começa com a mais recente. Mais
 * que isso é consulta à toa numa TV que fica o dia inteiro ligada.
 */
const RENOVAR_MS = 30 * 60_000;

/**
 * A música por baixo das fotos.
 *
 * Não desenha nada além do aviso de toque: a projeção é das fotos. O navegador
 * não deixa tocar som sem um gesto de alguém — pelo botão Iniciar há gesto;
 * pelo link, a TV começa sozinha e a primeira tentativa é recusada. Aí o aviso
 * aparece, e o toque que já pede tela cheia liga o som (`controle`).
 */
export function TrilhaSonora({
  escolha,
  aleatoria,
  repetir,
  pausada,
  controle,
}: {
  escolha: EscolhaDeMusica;
  aleatoria: boolean;
  repetir: boolean;
  /** Divulgação em vídeo com som na tela: a música espera e volta depois. */
  pausada: boolean;
  controle: React.MutableRefObject<ControleDaTrilha | null>;
}) {
  const entrada =
    escolha.tipo === "playlist"
      ? { playlistId: escolha.id }
      : escolha.tipo === "musicas"
        ? { musicaIds: escolha.ids }
        : null;

  const consulta = trpc.musicas.paraTocar.useQuery(entrada ?? { musicaIds: [1] }, {
    enabled: entrada !== null,
    refetchInterval: RENOVAR_MS,
    refetchOnWindowFocus: false,
  });
  const faixas = useMemo(() => consulta.data ?? [], [consulta.data]);
  const assinatura = faixas.map((f) => f.id).join(",");

  const [estado, setEstado] = useState<EstadoTocador>(TOCADOR_PARADO);
  // Cresce a cada avanço: é o que faz a mesma faixa tocar de novo quando a
  // playlist tem uma música só, e ela repete.
  const [passo, setPasso] = useState(0);
  const [bloqueada, setBloqueada] = useState(false);
  const iniciou = useRef(false);
  const falhasSeguidas = useRef(0);
  const audio = useRef<HTMLAudioElement>(null);

  // O que o avanço lê sem se recriar a cada renderização.
  const atual = useRef({ ids: [] as number[], modo: { aleatoria, repetir } });
  atual.current = { ids: faixas.map((f) => f.id), modo: { aleatoria, repetir } };

  useEffect(() => {
    const ids = atual.current.ids;
    if (ids.length === 0) return;
    if (!iniciou.current) {
      iniciou.current = true;
      setEstado(iniciarTocador(ids, atual.current.modo));
    } else {
      setEstado((e) => sincronizarFaixas(e, ids));
    }
  }, [assinatura]);

  const avancar = () => {
    setEstado((e) => proximaFaixa(e, atual.current.ids, atual.current.modo));
    setPasso((p) => p + 1);
  };

  const idAtual = faixaAtual(estado);
  const faixa = faixas.find((f) => f.id === idAtual) ?? null;

  // A URL fica a do começo da faixa — por isso só o id e o passo decidem, e não
  // a URL em si: a consulta renova as URLs, e trocar o `src` de um áudio que
  // está tocando o faz recomeçar do zero.
  const url = useMemo(() => faixa?.url ?? null, [faixa?.id, passo]);

  function tocar() {
    const el = audio.current;
    if (!el || !url || pausada) return;
    el.play()
      .then(() => setBloqueada(false))
      .catch((err: unknown) => {
        // Recusa por falta de gesto: espera o toque. Outra falha é da faixa,
        // e o `onError` do elemento cuida dela.
        if (err instanceof DOMException && err.name === "NotAllowedError") setBloqueada(true);
      });
  }

  useEffect(() => {
    const el = audio.current;
    if (!el || !url) return;
    if (pausada) {
      el.pause();
      return;
    }
    tocar();
    // O passo entra porque a mesma faixa repetida tem a mesma URL, e o
    // elemento novo (a `key` muda com o passo) também precisa começar.
  }, [url, pausada, passo]);

  // O toque na projeção vem por aqui, dentro do gesto — é o que o navegador exige.
  controle.current = { desbloquear: tocar };

  if (!url) return null;

  return (
    <>
      <audio
        key={`${idAtual}#${passo}`}
        ref={audio}
        src={url}
        preload="auto"
        onPlaying={() => {
          falhasSeguidas.current = 0;
        }}
        onEnded={avancar}
        onError={() => {
          // Uma faixa quebrada pula para a próxima; todas quebradas param, em
          // vez de a TV girar em falha para sempre.
          falhasSeguidas.current += 1;
          if (falhasSeguidas.current >= Math.max(faixas.length, 1)) {
            setEstado((e) => ({ ...e, acabou: true }));
            return;
          }
          setTimeout(avancar, 1000);
        }}
      />
      {bloqueada && (
        <p className="pointer-events-none fixed bottom-4 left-4 z-10 rounded-full bg-black/70 px-3 py-1.5 text-[0.8125rem] text-white/85">
          Toque na tela para ligar a música
        </p>
      )}
    </>
  );
}
